/**
 * useBoardDragListener - Generic drag lifecycle hook.
 *
 * Encapsulates the repeated pattern: isDragging state, body cursor style,
 * global mousemove/mouseup listeners, and cleanup on unmount.
 *
 * Usage:
 *   const { isDragging, startDrag } = useBoardDragListener('col-resize', onMove, onEnd);
 *   // In your start handler: set up refs, then call startDrag()
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { setDragBodyStyles, stopDragBodyStyles } from '../../lib/dragResizeUtils';

export function useBoardDragListener(
  cursor: 'row-resize' | 'col-resize',
  onMove: (e: MouseEvent) => void,
  onEnd?: () => void,
): { isDragging: boolean; startDrag: () => void } {
  const [isDragging, setIsDragging] = useState(false);

  // Store callbacks as refs so the effect doesn't re-run when they change
  const onMoveRef = useRef(onMove);
  const onEndRef = useRef(onEnd);
  onMoveRef.current = onMove;
  onEndRef.current = onEnd;

  const startDrag = useCallback(() => setIsDragging(true), []);

  useEffect(() => {
    if (!isDragging) return;

    setDragBodyStyles(cursor);

    const handleMouseMove = (e: MouseEvent) => onMoveRef.current(e);

    const handleMouseUp = () => {
      onEndRef.current?.();
      setIsDragging(false);
      stopDragBodyStyles();
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      stopDragBodyStyles();
    };
  }, [isDragging, cursor]);

  return { isDragging, startDrag };
}
